/*
 * Copyright 2026 Sony Corporation
 */
import { APP_WINDOW, type DesignOption, type Run } from '@ips-jit/schema';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '../contexts/auth-context';

/** The window the drafts are drawn for (the size the app opens at); previews scale it down. */
const FRAME = APP_WINDOW.initial;
/** The sizes the enlarged preview can show: the app's window as it opens and maximised on a full-HD screen. */
const SIZES = { initial: APP_WINDOW.initial, maximised: APP_WINDOW.maximised } as const;
type SizeName = keyof typeof SIZES;
const NONE = 'none';

/** A drafted page in a frame that runs nothing: the HTML has no scripts and the sandbox would not run them anyway. */
const Preview = ({
  html,
  scale,
  title,
  scrollable = false,
  frame = FRAME,
}: {
  html: string;
  scale: number;
  title: string;
  scrollable?: boolean;
  frame?: { width: number; height: number };
}) => (
  <Box sx={{ width: frame.width * scale, height: frame.height * scale, overflow: 'hidden', position: 'relative' }}>
    <Box
      component="iframe"
      title={title}
      sandbox=""
      srcDoc={html}
      sx={{
        width: frame.width,
        height: frame.height,
        border: 0,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        pointerEvents: scrollable ? 'auto' : 'none',
        position: 'absolute',
        top: 0,
        left: 0,
      }}
    />
  </Box>
);

/** The preview at the width of its card: the frame is scaled to whatever room the card has. */
const FittedPreview = ({ html, title }: { html: string; title: string }) => {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = box.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return (
    <Box ref={box} sx={{ width: '100%', aspectRatio: `${FRAME.width} / ${FRAME.height}` }}>
      {width > 0 && <Preview html={html} scale={width / FRAME.width} title={title} />}
    </Box>
  );
};

export interface DesignChoiceState {
  optionId: string;
  note: string;
}

/**
 * The design options drafted with a plan: the user picks the direction the page should take when approving. They show
 * a direction, not a specification, and say so.
 */
export const DesignOptions = ({
  projectId,
  run,
  choice,
  onChange,
  disabled,
}: {
  projectId: string;
  run: Run;
  choice: DesignChoiceState;
  onChange: (choice: DesignChoiceState) => void;
  disabled?: boolean;
}) => {
  const { api } = useAuth();
  const [html, setHtml] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<DesignOption | undefined>();
  const [size, setSize] = useState<SizeName>('initial');
  const options = run.design_options;

  useEffect(() => {
    let cancelled = false;
    for (const option of options.filter((o) => o.state === 'ready')) {
      api
        .getDesignPreview(projectId, run.run_id, option.option_id)
        .then((preview) => !cancelled && setHtml((old) => ({ ...old, [option.option_id]: preview.html })))
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [api, projectId, run.run_id, options]);

  const select = (optionId: string) => !disabled && onChange({ ...choice, optionId });

  return (
    <Stack spacing={1} aria-label="Design options">
      <Typography variant="subtitle2">Choose a design direction</Typography>
      <Typography variant="body2" color="text.secondary">
        These are directions, not exact screens: the app follows the layout and look you choose, and the details are
        settled while it is built. Click a preview to see it larger, and a card to choose it.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 1.5 }}>
        {options.map((option) => {
          const selected = choice.optionId === option.option_id;
          const ready = option.state === 'ready';
          return (
            <Card
              key={option.option_id}
              variant="outlined"
              sx={{ borderColor: selected ? 'primary.main' : undefined, borderWidth: selected ? 2 : 1 }}
            >
              {ready ? (
                html[option.option_id] ? (
                  <Box
                    component="button"
                    type="button"
                    aria-label={`Enlarge ${option.title}`}
                    onClick={() => setOpen(option)}
                    sx={{ display: 'block', width: '100%', p: 0, border: 0, cursor: 'zoom-in', background: 'none' }}
                  >
                    <FittedPreview html={html[option.option_id]} title={option.title} />
                  </Box>
                ) : (
                  <Skeleton
                    variant="rectangular"
                    sx={{ aspectRatio: `${FRAME.width} / ${FRAME.height}`, height: 'auto' }}
                  />
                )
              ) : (
                <Alert severity="warning" sx={{ m: 1.5, py: 0 }}>
                  This option could not be drawn.
                </Alert>
              )}
              <CardActionArea
                disabled={!ready || disabled}
                onClick={() => select(option.option_id)}
                aria-pressed={selected}
                aria-label={`Choose ${option.title}`}
                sx={{ p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}
              >
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    {option.title}
                  </Typography>
                  <Chip size="small" label={option.direction} color={selected ? 'primary' : 'default'} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {option.concept}
                </Typography>
              </CardActionArea>
            </Card>
          );
        })}
        <Card variant="outlined" sx={{ borderColor: choice.optionId === NONE ? 'primary.main' : undefined }}>
          <CardActionArea
            disabled={disabled}
            onClick={() => select(NONE)}
            aria-pressed={choice.optionId === NONE}
            sx={{ p: 1.5, height: '100%' }}
          >
            <Typography variant="subtitle2">None of these</Typography>
            <Typography variant="body2" color="text.secondary">
              Build from the plan alone. To describe another look, reject the plan and say what you want.
            </Typography>
          </CardActionArea>
        </Card>
      </Box>
      <TextField
        size="small"
        placeholder="Optional note, e.g. “this layout, but lighter colours”"
        value={choice.note}
        disabled={disabled}
        onChange={(event) => onChange({ ...choice, note: event.target.value })}
        slotProps={{ htmlInput: { 'aria-label': 'Design note', maxLength: 500 } }}
      />
      <Dialog open={open !== undefined} onClose={() => setOpen(undefined)} maxWidth={false}>
        {open && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                {open.title} · {open.direction}
              </Box>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={size}
                onChange={(_event, value: SizeName | null) => value && setSize(value)}
                aria-label="Window size"
              >
                {(Object.keys(SIZES) as SizeName[]).map((name) => (
                  <ToggleButton key={name} value={name}>
                    {`${SIZES[name].width}×${SIZES[name].height}`}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </DialogTitle>
            <DialogContent>
              {/* The whole window at once, scaled to the dialog: whether it fits is the point of looking. */}
              <Preview
                html={html[open.option_id] ?? ''}
                frame={SIZES[size]}
                scale={Math.min(
                  1,
                  (typeof window === 'undefined' ? 1600 : window.innerWidth * 0.85) / SIZES[size].width
                )}
                title={`${open.title}, ${SIZES[size].width}×${SIZES[size].height}`}
                scrollable
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </Stack>
  );
};

/** What the approval sends: a drafted option, or nothing for "none of these" (the note then goes with no option). */
export const designChoiceOf = (choice: DesignChoiceState) =>
  choice.optionId && choice.optionId !== NONE
    ? { option_id: choice.optionId, ...(choice.note.trim() ? { note: choice.note.trim() } : {}) }
    : undefined;
